import { noop } from '@/utils'
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import { E164Number } from 'libphonenumber-js'
import { useCallback, useMemo, useState } from 'react'
import PhoneInputComponent, {
  Country,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input/input'
import 'react-phone-number-input/style.css'
import { GroupInputContainer, Input } from '../../styled'
import { Option, Select } from '../Select'

const prefixWithPlus = (val: string) =>
  val && !val.startsWith('+') ? `+${val}` : val

const omitPlus = (val: string) => val.replace('+', '')

const getDefaultCountry = (
  value: string,
  defaultValue: Country = 'DE',
): Country => {
  const safeValue = (value || '').slice(0, 3).padEnd(5, '0')
  const parsed = parsePhoneNumber(safeValue)

  return parsed?.country || defaultValue
}

export interface PhoneInputProps {
  defaultCountry?: Country
  international?: boolean
  value?: string
  disabled?: boolean
  onChange?: (value: string) => void
  onBlur?: (isValid: boolean) => void
}

export const PhoneInput = ({
  defaultCountry = 'DE',
  international = true,
  value = '',
  onChange = noop,
  onBlur,
  disabled,
  ...rest
}: PhoneInputProps) => {
  const [country, setCountry] = useState<Country | undefined>(
    getDefaultCountry(value, defaultCountry),
  )

  const isValid = useMemo(() => {
    const fullValue = prefixWithPlus(value)
    return fullValue ? isValidPhoneNumber(fullValue) : true
  }, [value])

  const handleOnChange = useCallback(
    (newValue?: E164Number) => {
      if (!onChange) return
      const stripped = newValue ? omitPlus(newValue) : ''
      onChange(stripped)
    },
    [onChange],
  )

  const handleBlur = useCallback(() => {
    if (onBlur) onBlur(isValid)
  }, [onBlur, isValid])

  const handleOnSelect = (value?: string) => {
    if (value) setCountry(value as Country)
  }

  return (
    <GroupInputContainer>
      <Select
        autocomplete={false}
        disabled={disabled}
        onSelect={handleOnSelect}
        readOnly
        value={country}
      >
        {getCountries().map((item) => (
          <Option key={item} value={item}>
            {`${getUnicodeFlagIcon(item)} +${getCountryCallingCode(item)}`}
          </Option>
        ))}
      </Select>
      <PhoneInputComponent
        {...rest}
        country={country}
        disabled={disabled}
        inputComponent={Input}
        international={international}
        onBlur={handleBlur}
        onChange={handleOnChange}
        placeholder="0123 456 7890"
        error={!isValid}
        value={prefixWithPlus(value)}
      />
    </GroupInputContainer>
  )
}
